import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { TaskProgress } from '../components/tasks';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Tab,
  Tabs,
  LinearProgress,
  Snackbar,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Schedule as ScheduleIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Language as LanguageIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Article as ArticleIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  EmojiEvents as AwardsIcon,
  PictureAsPdf as PdfIcon,
  LinkOutlined as LinkIcon,
  SkipNext as SkipIcon,
  HourglassEmpty as ProcessingIcon,
  Groups as TotalLeadsIcon,
  LocationOn as LocationIcon,
  AccountBalance as FinanceIcon,
  Category as CategoryIcon,
  Timeline as TimelineIcon,
  Gavel as LicenseIcon,
  PieChart as ShareholdingIcon,
  TrendingUp as TrendingIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
} from '@mui/icons-material';
import { jobsAPI, apiUsageAPI, leadFlagsAPI, gebizAPI, enrichmentSessionAPI } from '../services/api';
// Add helper imports
import Link from '@mui/material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
// Import new components
import LeadFlags from '../components/leads/LeadFlags';
import CustomExportDialog from '../components/leads/CustomExportDialog';
// Import GeBIZ components
import { GeBIZSummary, GeBIZAwardsTable, FuzzyReviewDialog, PDFSelectionDialog } from '../components/gebiz';
// Import SGPBusiness components
import SGPBusinessOverviewCard from '../components/sgpbusiness/SGPBusinessOverviewCard';
// Import Leads components
import { LeadsTable, LeadAwardsDialog, LeadNotesDialog } from '../components/leads';
// Import Enrichment components
import { InterventionDialog, EnrichmentSessionPanel } from '../components/enrichment';

// ============================================
// TAB PANEL COMPONENT
// ============================================
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`job-tabpanel-${index}`}
      aria-labelledby={`job-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// ============================================
// STATUS CHIP COMPONENT
// ============================================
const StatusChip = ({ status }) => {
  const statusLower = status?.toLowerCase();
  const getStatusProps = () => {
    switch (statusLower) {
      case 'completed':
        return { color: 'success', label: 'Completed' };
      case 'failed':
        return { color: 'error', label: 'Failed' };
      case 'processing':
        return { color: 'warning', label: 'Processing' };
      case 'pending':
        return { color: 'warning', label: 'Pending' };
      default:
        return { color: 'default', label: status || 'Unknown' };
    }
  };
  const props = getStatusProps();
  return <Chip label={props.label} color={props.color} size="small" />;
};

// ============================================
// TYPE CHIP COMPONENT
// ============================================
const TypeChip = ({ jobType }) => {
  const typeUpper = jobType?.toUpperCase();
  const getTypeLabel = () => {
    switch (typeUpper) {
      case 'TEXT_FILE':
        return 'Text File';
      case 'MANUAL_EXTRACTION':
        return 'Manual Extraction';
      case 'EXCEL_FILE':
        return 'Excel File';
      case 'JSON_FILE':
        return 'JSON File';
      case 'WEB_SCRAPING':
        return 'Web Scraping';
      default:
        return jobType || 'Unknown';
    }
  };
  return (
    <Chip 
      label={getTypeLabel()} 
      size="small" 
      variant="outlined"
      sx={{ backgroundColor: '#f5f5f5', borderColor: '#bdbdbd' }}
    />
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Leads filter state (server-side filtering)
  const [leadsSearchQuery, setLeadsSearchQuery] = useState('');
  const [leadsStatusFilter, setLeadsStatusFilter] = useState('all');
  const [leadsEnrichmentFilter, setLeadsEnrichmentFilter] = useState('all');
  const [leadsSourceFilter, setLeadsSourceFilter] = useState('all');
  
  // Debounced search to avoid too many API calls
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(leadsSearchQuery);
      setPage(1); // Reset to first page when search changes
    }, 300);
    return () => clearTimeout(timer);
  }, [leadsSearchQuery]);
  
  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [leadsStatusFilter, leadsEnrichmentFilter, leadsSourceFilter]);
  
  // Lead detail dialog state
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetailOpen, setLeadDetailOpen] = useState(false);

  // Lead awards dialog state
  const [awardsDialogOpen, setAwardsDialogOpen] = useState(false);
  const [selectedLeadForAwards, setSelectedLeadForAwards] = useState(null);

  // Lead notes dialog state
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedLeadForNotes, setSelectedLeadForNotes] = useState(null);

  // New feature states
  const [selectedLeads, setSelectedLeads] = useState([]); // For bulk selection
  // eslint-disable-next-line no-unused-vars
  const [selectAllInJob, setSelectAllInJob] = useState(false); // Track if all leads in job are selected
  const [bulkExportOpen, setBulkExportOpen] = useState(false);
  const [singleLeadExportOpen, setSingleLeadExportOpen] = useState(false);
  
  // Filtered custom export state (for exporting all filtered leads from Leads tab)
  const [filteredExportOpen, setFilteredExportOpen] = useState(false);
  const [filteredExportLeadIds, setFilteredExportLeadIds] = useState([]);
  const [loadingFilteredExport, setLoadingFilteredExport] = useState(false);

  // GeBIZ state
  const [gebizStatusFilter, setGebizStatusFilter] = useState('');
  const [gebizSearchQuery, setGebizSearchQuery] = useState('');
  const [debouncedGebizSearch, setDebouncedGebizSearch] = useState('');
  const [gebizPage, setGebizPage] = useState(0);
  const [gebizRowsPerPage, setGebizRowsPerPage] = useState(10);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedAward, setSelectedAward] = useState(null);
  
  // Debounce GeBIZ search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedGebizSearch(gebizSearchQuery);
      setGebizPage(0); // Reset to first page when search changes
    }, 300);
    return () => clearTimeout(timer);
  }, [gebizSearchQuery]);
  
  // Reset GeBIZ page when status filter changes
  useEffect(() => {
    setGebizPage(0);
  }, [gebizStatusFilter]);
  
  // Task trigger state
  const [taskSnackbar, setTaskSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState(null);  // Track specific task for progress
  
  // Enrichment dialog state
  const [enrichDialogOpen, setEnrichDialogOpen] = useState(false);
  const [skipRecordOwlAuth, setSkipRecordOwlAuth] = useState(false);
  const [enrichingLeadId, setEnrichingLeadId] = useState(null);  // Track which lead is being enriched
  // Note: Session state is managed by EnrichmentSessionPanel component

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [completionFilter, setCompletionFilter] = useState([0, 100]);
  const [foundersFilter, setFoundersFilter] = useState('all');
  const [websiteFilter, setWebsiteFilter] = useState('all');
  const [fieldCompletionFilter, setFieldCompletionFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [filterLogic, setFilterLogic] = useState('and');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFlagFilter, setSelectedFlagFilter] = useState(null); // Flag filter

  // Load saved filters from localStorage
  useEffect(() => {
    const savedFilters = localStorage.getItem(`jobDetailFilters_${id}`);
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        setSearchTerm(filters.searchTerm || '');
        setStatusFilter(filters.statusFilter || 'all');
        setCompletionFilter(filters.completionFilter || [0, 100]);
        setFoundersFilter(filters.foundersFilter || 'all');
        setWebsiteFilter(filters.websiteFilter || 'all');
        setFieldCompletionFilter(filters.fieldCompletionFilter || 'all');
        setDateRange(filters.dateRange || { start: '', end: '' });
        setFilterLogic(filters.filterLogic || 'and');
        setShowFilters(filters.showFilters || false);
        setSelectedFlagFilter(filters.selectedFlagFilter || null);
      } catch (error) {
        console.error('Error loading saved filters:', error);
      }
    }
  }, [id]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    const filters = {
      searchTerm,
      statusFilter,
      completionFilter,
      foundersFilter,
      websiteFilter,
      fieldCompletionFilter,
      dateRange,
      filterLogic,
      showFilters,
      selectedFlagFilter,
    };
    localStorage.setItem(`jobDetailFilters_${id}`, JSON.stringify(filters));
  }, [searchTerm, statusFilter, completionFilter, foundersFilter, websiteFilter, fieldCompletionFilter, dateRange, filterLogic, showFilters, selectedFlagFilter, id]);

  // Debug: Monitor selectedLeads changes
  useEffect(() => {
    console.log('🔴 selectedLeads state changed:', {
      length: selectedLeads.length,
      selectAllInJob,
      ids: selectedLeads.slice(0, 5), // Show first 5 IDs
      timestamp: new Date().toISOString()
    });
  }, [selectedLeads, selectAllInJob]);

  const { data: job, isLoading, error } = useQuery(
    ['job', id],
    () => jobsAPI.getJob(id)
  );

  const { data: usageLogs } = useQuery(
    ['jobUsage', id],
    () => apiUsageAPI.getJobUsage(id)
  );

  // Get leads count
  // Count query params (same filters, no pagination)
  const leadsCountParams = {
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(leadsStatusFilter !== 'all' && { status: leadsStatusFilter }),
    ...(leadsEnrichmentFilter !== 'all' && { enrichment_status: leadsEnrichmentFilter }),
    ...(leadsSourceFilter !== 'all' && { source: leadsSourceFilter }),
  };

  const { data: leadsCount } = useQuery(
    ['jobLeadsCount', id, debouncedSearch, leadsStatusFilter, leadsEnrichmentFilter, leadsSourceFilter],
    () => jobsAPI.getJobLeadsCount(id, leadsCountParams)
  );

  // Get job statistics (accurate counts from leads_enriched table)
  const { data: jobStats } = useQuery(
    ['jobStats', id],
    () => jobsAPI.getJobStats(id),
    { enabled: !!id }
  );

  // Get GeBIZ awards count
  const { data: gebizSummary } = useQuery(
    ['gebizSummary', id],
    () => gebizAPI.getAwardsSummary(id),
    { enabled: !!id }
  );

  // Get GeBIZ awards with pagination and search (server-side filtering)
  const { 
    data: gebizAwards, 
    isLoading: gebizAwardsLoading, 
    error: gebizAwardsError,
    refetch: refetchGebizAwards 
  } = useQuery(
    ['gebizAwards', id, gebizStatusFilter, debouncedGebizSearch, gebizPage, gebizRowsPerPage],
    () => gebizAPI.getAwards(id, {
      match_status: gebizStatusFilter || undefined,
      search: debouncedGebizSearch || undefined,
      skip: gebizPage * gebizRowsPerPage,
      limit: gebizRowsPerPage,
    }),
    { 
      enabled: !!id,
      keepPreviousData: true,
    }
  );

  // Get GeBIZ awards count (same filters as awards list)
  const { data: gebizAwardsCount } = useQuery(
    ['gebizAwardsCount', id, gebizStatusFilter, debouncedGebizSearch],
    () => gebizAPI.getAwardsCount(id, {
      match_status: gebizStatusFilter || undefined,
      search: debouncedGebizSearch || undefined,
    }),
    { enabled: !!id }
  );

  // Get available flags (used by LeadFlags component)
  useQuery(
    ['flags'],
    () => leadFlagsAPI.getFlags(),
    { staleTime: 5 * 60 * 1000 } // Cache for 5 minutes
  );
  
  // Get active Browserless session for this job (batch session architecture)
  // Note: Local session file check removed - Cloudflare blocks local Playwright anyway
  const { 
    data: browserlessSession,
    isLoading: browserlessLoading,
    isError: browserlessError,
    refetch: refetchBrowserlessSession
  } = useQuery(
    ['enrichmentSession', id],
    () => enrichmentSessionAPI.getJobSession(id),
    { 
      staleTime: 10 * 1000, // Cache for 10 seconds (session state can change)
      enabled: enrichDialogOpen, // Only fetch when dialog is open
      retry: false, // Don't retry - 404 means no session
    }
  );
  
  // Check Redis cookies for this job (persist for ~2 hours after login)
  // This allows enrichment even after Browserless session times out (15 min)
  const { 
    data: redisCookies,
    isLoading: redisCookiesLoading,
    refetch: refetchRedisCookies
  } = useQuery(
    ['redisCookies', id],
    () => enrichmentSessionAPI.checkCookies(id),
    { 
      staleTime: 30 * 1000, // Cache for 30 seconds
      enabled: enrichDialogOpen, // Only fetch when dialog is open
      retry: false,
    }
  );
  
  // Determine if we have a valid Browserless session
  const hasBrowserlessSession = browserlessSession?.status === 'ready' && browserlessSession?.logged_in;
  
  // Determine if we have valid Redis cookies (from previous login)
  const hasRedisCookies = redisCookies?.valid === true;
  
  // Can enrich if we have active session OR valid Redis cookies
  const hasValidAuth = hasBrowserlessSession || hasRedisCookies;

  // Get paginated leads (revert to original approach)
  // Build filter params for API
  const leadsFilterParams = {
    skip: (page - 1) * pageSize,
    limit: pageSize,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(leadsStatusFilter !== 'all' && { status: leadsStatusFilter }),
    ...(leadsEnrichmentFilter !== 'all' && { enrichment_status: leadsEnrichmentFilter }),
    ...(leadsSourceFilter !== 'all' && { source: leadsSourceFilter }),
  };

  const { data: leads, isLoading: leadsLoading, error: leadsError } = useQuery(
    ['jobLeads', id, page, pageSize, debouncedSearch, leadsStatusFilter, leadsEnrichmentFilter, leadsSourceFilter],
    () => jobsAPI.getJobLeads(id, leadsFilterParams)
  );

  console.log('Leads data:', leads);
  console.log('Leads loading:', leadsLoading);
  console.log('Leads error:', leadsError);

  useEffect(() => {
    console.log('Current page:', page);
    console.log('Leads for this page:', leads);
  }, [page, leads]);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(searchTerm || statusFilter !== 'all' || completionFilter[0] > 0 || completionFilter[1] < 100 || 
              foundersFilter !== 'all' || websiteFilter !== 'all' || fieldCompletionFilter !== 'all' || dateRange.start || dateRange.end || selectedFlagFilter);
  }, [searchTerm, statusFilter, completionFilter, foundersFilter, websiteFilter, fieldCompletionFilter, dateRange, selectedFlagFilter]);

  // Get all leads for client-side filtering (fetch in chunks)
  const { data: allLeads } = useQuery(
    ['jobLeadsAll', id],
    async () => {
      // Fetch all leads in chunks of 100 (API limit)
      const allLeadsData = [];
      let skip = 0;
      const chunkSize = 100;
      
      while (true) {
        const chunk = await jobsAPI.getJobLeads(id, { skip, limit: chunkSize });
        if (chunk.length === 0) break;
        allLeadsData.push(...chunk);
        skip += chunkSize;
        
        // Safety check to prevent infinite loop
        if (skip > 10000) break;
      }
      
      return allLeadsData;
    },
    {
      // Only fetch all leads when filters are active
      enabled: hasActiveFilters
    }
  );

  // Get detailed lead data when dialog opens
  const { data: detailedLead } = useQuery(
    ['jobLead', id, selectedLead?.id],
    () => jobsAPI.getJobLead(id, selectedLead?.id),
    { enabled: !!selectedLead }
  );

  const startJobMutation = useMutation(jobsAPI.startJob, {
    onSuccess: () => {
      queryClient.invalidateQueries(['job', id]);
      queryClient.invalidateQueries('jobs');
    },
  });

  const deleteJobMutation = useMutation(jobsAPI.deleteJob, {
    onSuccess: () => {
      navigate('/jobs');
    },
  });

  // Task trigger mutations
  const extractionMutation = useMutation(
    (files) => gebizAPI.triggerExtraction(id, files),
    {
      onSuccess: (data) => {
        setPdfDialogOpen(false); // Close the dialog
        // Store the task ID for direct tracking (no race condition!)
        const taskId = parseInt(data.task_id, 10);
        setCurrentTaskId(taskId);
        // No snackbar needed - TaskProgress shows immediate feedback
        // Invalidate queries to refresh data
        queryClient.invalidateQueries(['task', taskId]);
        queryClient.invalidateQueries(['jobPDFs', id]);
      },
      onError: (error) => {
        setTaskSnackbar({ 
          open: true, 
          message: `Failed to start extraction: ${error.response?.data?.detail || error.message}`, 
          severity: 'error' 
        });
      },
    }
  );

  const matchingMutation = useMutation(
    () => gebizAPI.triggerMatching(id),
    {
      onSuccess: (data) => {
        // Handle "skipped" status (no pending awards)
        if (data.status === 'skipped') {
          setTaskSnackbar({ 
            open: true, 
            message: data.message || 'No pending awards to match', 
            severity: 'info' 
          });
          return;
        }
        // Store the task ID for direct tracking (no race condition!)
        const taskId = parseInt(data.task_id, 10);
        setCurrentTaskId(taskId);
        // No snackbar needed - TaskProgress shows immediate feedback
        // Invalidate queries to refresh data
        queryClient.invalidateQueries(['task', taskId]);
      },
      onError: (error) => {
        setTaskSnackbar({ 
          open: true, 
          message: `Failed to start matching: ${error.response?.data?.detail || error.message}`, 
          severity: 'error' 
        });
      },
    }
  );

  const enrichmentMutation = useMutation(
    ({ leadId, skipAuth }) => {
      // Track the lead being enriched for intervention polling
      setEnrichingLeadId(leadId);
      return gebizAPI.triggerEnrichment(id, leadId, skipAuth);
    },
    {
      onSuccess: (data) => {
        // Close the dialog
        setEnrichDialogOpen(false);
        // Store the task ID for direct tracking (no race condition!)
        const taskId = parseInt(data.task_id, 10);
        setCurrentTaskId(taskId);
        // No snackbar needed - TaskProgress shows immediate feedback
        // Invalidate queries to refresh data
        queryClient.invalidateQueries(['task', taskId]);
        queryClient.invalidateQueries(['jobLeads', id]);
        queryClient.invalidateQueries(['enrichmentSession', id]); // Refresh Browserless session status
      },
      onError: (error) => {
        // Clear enriching lead on error
        setEnrichingLeadId(null);
        // Handle session validation errors specially
        const detail = error.response?.data?.detail;
        if (detail?.error === 'No active Browserless session' || detail?.error === 'No active Browserless session or valid cookies') {
          setTaskSnackbar({ 
            open: true, 
            message: `${detail.reason}. ${detail.hint}`, 
            severity: 'warning' 
          });
          // Refresh session and cookie status
          refetchBrowserlessSession();
          refetchRedisCookies();
        } else {
          setTaskSnackbar({ 
            open: true, 
            message: `Failed to start enrichment: ${typeof detail === 'string' ? detail : error.message}`, 
            severity: 'error' 
          });
        }
      },
    }
  );

  // Skip/Unskip mutations
  const [skipLoading, setSkipLoading] = useState(false);

  const skipLeadMutation = useMutation(
    ({ leadId, reason }) => jobsAPI.skipLead(id, leadId, reason),
    {
      onSuccess: (data) => {
        setTaskSnackbar({ 
          open: true, 
          message: data.message || 'Lead skipped successfully', 
          severity: 'success' 
        });
        queryClient.invalidateQueries(['jobLeads', id]);
        queryClient.invalidateQueries(['jobLeadsCount', id]);
      },
      onError: (error) => {
        setTaskSnackbar({ 
          open: true, 
          message: `Failed to skip lead: ${error.response?.data?.detail || error.message}`, 
          severity: 'error' 
        });
      },
    }
  );

  const unskipLeadMutation = useMutation(
    (leadId) => jobsAPI.unskipLead(id, leadId),
    {
      onSuccess: (data) => {
        setTaskSnackbar({ 
          open: true, 
          message: data.message || 'Lead unskipped successfully', 
          severity: 'success' 
        });
        queryClient.invalidateQueries(['jobLeads', id]);
        queryClient.invalidateQueries(['jobLeadsCount', id]);
      },
      onError: (error) => {
        setTaskSnackbar({ 
          open: true, 
          message: `Failed to unskip lead: ${error.response?.data?.detail || error.message}`, 
          severity: 'error' 
        });
      },
    }
  );

  const skipLeadsBatchMutation = useMutation(
    ({ leadIds, reason }) => jobsAPI.skipLeadsBatch(id, leadIds, reason),
    {
      onSuccess: (data) => {
        setTaskSnackbar({ 
          open: true, 
          message: `${data.message}. Skipped: ${data.skipped_count}, Already skipped: ${data.already_skipped.length}, Not pending: ${data.not_pending.length}`, 
          severity: data.skipped_count > 0 ? 'success' : 'info' 
        });
        queryClient.invalidateQueries(['jobLeads', id]);
        queryClient.invalidateQueries(['jobLeadsCount', id]);
        setSelectedLeads([]); // Clear selection after bulk operation
      },
      onError: (error) => {
        setTaskSnackbar({ 
          open: true, 
          message: `Failed to skip leads: ${error.response?.data?.detail || error.message}`, 
          severity: 'error' 
        });
      },
    }
  );

  const unskipLeadsBatchMutation = useMutation(
    (leadIds) => jobsAPI.unskipLeadsBatch(id, leadIds),
    {
      onSuccess: (data) => {
        setTaskSnackbar({ 
          open: true, 
          message: `${data.message}. Unskipped: ${data.unskipped_count}, Already pending: ${data.already_pending.length}`, 
          severity: data.unskipped_count > 0 ? 'success' : 'info' 
        });
        queryClient.invalidateQueries(['jobLeads', id]);
        queryClient.invalidateQueries(['jobLeadsCount', id]);
        setSelectedLeads([]); // Clear selection after bulk operation
      },
      onError: (error) => {
        setTaskSnackbar({ 
          open: true, 
          message: `Failed to unskip leads: ${error.response?.data?.detail || error.message}`, 
          severity: 'error' 
        });
      },
    }
  );

  // Skip/Unskip handlers for LeadsTable
  const handleSkipLead = async (leadId, reason) => {
    setSkipLoading(true);
    try {
      await skipLeadMutation.mutateAsync({ leadId, reason });
    } finally {
      setSkipLoading(false);
    }
  };

  const handleUnskipLead = async (leadId) => {
    setSkipLoading(true);
    try {
      await unskipLeadMutation.mutateAsync(leadId);
    } finally {
      setSkipLoading(false);
    }
  };

  const handleSkipSelectedLeads = async (leadIds, reason) => {
    setSkipLoading(true);
    try {
      await skipLeadsBatchMutation.mutateAsync({ leadIds, reason });
    } finally {
      setSkipLoading(false);
    }
  };

  const handleUnskipSelectedLeads = async (leadIds) => {
    setSkipLoading(true);
    try {
      await unskipLeadsBatchMutation.mutateAsync(leadIds);
    } finally {
      setSkipLoading(false);
    }
  };

  const handleStartJob = () => {
    startJobMutation.mutate(id);
  };

  const handleDeleteJob = () => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      deleteJobMutation.mutate(id);
    }
  };

  const handleGebizRefresh = () => {
    queryClient.invalidateQueries(['gebizAwards', id]);
    queryClient.invalidateQueries(['gebizAwardsCount', id]);
    queryClient.invalidateQueries(['gebizSummary', id]);
    refetchGebizAwards();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };


  // Extract filtering logic to avoid duplication
  const applyFiltersToLead = useCallback((lead) => {
    const conditions = [];
    
    // Search condition
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const companyName = (lead.company_name || '').toLowerCase();
      const businessInfo = (() => {
        try {
          const info = Array.isArray(lead.business_info) && lead.business_info[0] ? 
            lead.business_info[0].value : 
            lead.business_info;
          return typeof info === 'string' ? info.toLowerCase() : String(info).toLowerCase();
        } catch (error) {
          return '';
        }
      })();
      const matchesSearch = companyName.includes(searchLower) || businessInfo.includes(searchLower);
      conditions.push(matchesSearch);
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      conditions.push(lead.status === statusFilter);
    }
    
    // Completion percentage filter
    if (completionFilter[0] > 0 || completionFilter[1] < 100) {
      const completion = lead.data_quality_score || 0;
      conditions.push(completion >= completionFilter[0] && completion <= completionFilter[1]);
    }
    
    // Founder info filter
    if (foundersFilter !== 'all') {
      const hasMeaningfulFounders = (() => {
        try {
          if (!lead.founders || !Array.isArray(lead.founders) || lead.founders.length === 0) {
            return false;
          }
          
          const invalidValues = ['not found', 'not available', '', 'N/A', 'n/a', 'Not Available',
                               'null', 'None', 'undefined', 'no data', 'no information',
                               'unknown', 'not provided', 'not specified', 'not applicable'];
          
          return lead.founders.some(founder => {
            // Check if any founder has meaningful data in any field
            const founderFields = ['name', 'background', 'emails', 'phones', 'news'];
            return founderFields.some(field => {
              if (founder[field] && Array.isArray(founder[field]) && founder[field].length > 0) {
                const value = founder[field][0]?.value;
                return value && typeof value === 'string' && 
                       value.trim() !== '' && 
                       !invalidValues.includes(value.toLowerCase().trim());
              }
              return false;
            });
          });
        } catch (error) {
          return false;
        }
      })();
      
      if (foundersFilter === 'has') {
        conditions.push(hasMeaningfulFounders);
      } else if (foundersFilter === 'none') {
        conditions.push(!hasMeaningfulFounders);
      }
    }
    
    // Website presence filter
    if (websiteFilter !== 'all') {
      const hasWebsite = (() => {
        try {
          const website = Array.isArray(lead.company_website) && lead.company_website[0] ? 
            lead.company_website[0].value : 
            lead.company_website;
          return typeof website === 'string' && website.trim() !== '' && website !== 'not found';
        } catch (error) {
          return false;
        }
      })();
      if (websiteFilter === 'has') {
        conditions.push(hasWebsite);
      } else if (websiteFilter === 'none') {
        conditions.push(!hasWebsite);
      }
    }
    
    // Field completion filter
    if (fieldCompletionFilter !== 'all') {
      const fieldsCompleted = lead.fields_completed || 0;
      const totalFields = lead.total_fields || 1;
      const completionRatio = fieldsCompleted / totalFields;
      if (fieldCompletionFilter === 'complete') {
        conditions.push(completionRatio === 1);
      } else if (fieldCompletionFilter === 'partial') {
        conditions.push(completionRatio > 0 && completionRatio < 1);
      } else if (fieldCompletionFilter === 'none') {
        conditions.push(completionRatio === 0);
      }
    }
    
    // Date range filter
    if (dateRange.start || dateRange.end) {
      const processedDate = lead.processed_at ? new Date(lead.processed_at) : null;
      if (processedDate) {
        if (dateRange.start) {
          conditions.push(processedDate >= new Date(dateRange.start));
        }
        if (dateRange.end) {
          conditions.push(processedDate <= new Date(dateRange.end + 'T23:59:59'));
        }
      } else {
        conditions.push(false); // No processed date
      }
    }
    
    // Flag filter
    if (selectedFlagFilter) {
      const hasSelectedFlag = lead.flags && Array.isArray(lead.flags) && 
        lead.flags.some(flag => flag.id === selectedFlagFilter);
      conditions.push(hasSelectedFlag);
    }
    
    // Apply logic (AND or OR)
    if (conditions.length === 0) return true;
    if (filterLogic === 'and') {
      return conditions.every(condition => condition);
    } else {
      return conditions.some(condition => condition);
    }
  }, [searchTerm, statusFilter, completionFilter, foundersFilter, websiteFilter, fieldCompletionFilter, dateRange, filterLogic, selectedFlagFilter]);

  // Determine which leads to display in the UI
  // eslint-disable-next-line no-unused-vars
  const leadsToDisplay = useMemo(() => {
    if (!hasActiveFilters) {
      return leads || [];
    }
    // If filters are active but allLeads is not loaded yet, return empty array
    if (!allLeads) return [];
    // Apply filters to all leads using the extracted function
    const filtered = allLeads.filter(applyFiltersToLead);
    // Client-side pagination for filtered results
    const startIndex = (page - 1) * pageSize;
    return filtered.slice(startIndex, startIndex + pageSize);
  }, [hasActiveFilters, leads, allLeads, page, pageSize, applyFiltersToLead]);

  const handleLeadClick = (lead) => {
    console.log('Lead data structure:', lead);
    console.log('Founders data:', lead.founders);
    console.log('Founders type:', typeof lead.founders);
    console.log('Founders is array:', Array.isArray(lead.founders));
    if (lead.founders && Array.isArray(lead.founders)) {
      console.log('First founder item:', lead.founders[0]);
    }
    setSelectedLead(lead);
    setLeadDetailOpen(true);
  };

  const handleCloseLeadDetail = () => {
    setLeadDetailOpen(false);
    setSelectedLead(null);
  };

  // Handler for View Awards dialog
  const handleViewAwards = (lead) => {
    setSelectedLeadForAwards(lead);
    setAwardsDialogOpen(true);
  };

  // Handler for View Notes dialog
  const handleViewNotes = (lead) => {
    setSelectedLeadForNotes(lead);
    setNotesDialogOpen(true);
  };

  // Handlers for new features
  const handleToggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  // Special handling for founders data
  const formatFounders = (value) => {
    try {
      if (!value) return 'Not available';
      
      // Handle arrays of founders (like other fields with value/source structure)
      if (Array.isArray(value)) {
        if (value.length === 0) return 'Not available';
        
        const founderInfo = value.map((founder, index) => {
          if (typeof founder === 'object' && founder !== null) {
            // Handle the value/source structure like other fields
            if (founder.value !== undefined) {
              return String(founder.value);
            }
            
            // Extract all available information from the founder object
            const sections = [];
            
            // Helper function to extract value from nested array structure
            const extractValue = (field) => {
              if (!field) return null;
              if (Array.isArray(field)) {
                if (field.length > 0) {
                  const fieldValue = field[0];
                  if (typeof fieldValue === 'object' && fieldValue.value !== undefined) {
                    return fieldValue.value;
                  } else {
                    return String(fieldValue);
                  }
                }
              } else {
                return String(field);
              }
              return null;
            };
            
            // Name section
            const name = extractValue(founder.name);
            if (name && name !== 'not available') {
              sections.push(`Name: ${name}`);
            } else {
              sections.push('Name: Not available');
            }
            
            // Background section
            const background = extractValue(founder.background);
            if (background && background !== 'not available') {
              sections.push(`Background: ${background}`);
            } else {
              sections.push('Background: Not available');
            }
            
            // Emails section
            const emails = extractValue(founder.emails);
            if (emails && emails !== 'not available') {
              sections.push(`Emails: ${emails}`);
            } else {
              sections.push('Emails: Not available');
            }
            
            // Phones section
            const phones = extractValue(founder.phones);
            if (phones && phones !== 'not available') {
              sections.push(`Phones: ${phones}`);
            } else {
              sections.push('Phones: Not available');
            }
            
            // News section
            const news = extractValue(founder.news);
            if (news && news !== 'not available') {
              sections.push(`News: ${news}`);
            } else {
              sections.push('News: Not available');
            }
            
            // Additional fields that might exist
            const title = extractValue(founder.title);
            if (title && title !== 'not available') {
              sections.push(`Title: ${title}`);
            }
            
            const role = extractValue(founder.role);
            if (role && role !== 'not available') {
              sections.push(`Role: ${role}`);
            }
            
            const position = extractValue(founder.position);
            if (position && position !== 'not available') {
              sections.push(`Position: ${position}`);
            }
            
            if (sections.length > 0) {
              return sections.join('\n');
            }
            
            // If no structured data found, show as JSON
            return JSON.stringify(founder, null, 2);
          }
          return String(founder);
        });
        
        return founderInfo.join('\n\n---\n\n');
      }
      
      // Handle single founder object
      if (typeof value === 'object' && value !== null) {
        // Handle the value/source structure
        if (value.value !== undefined) {
          return String(value.value);
        }
        
        // Extract all available information from the single founder object
        const sections = [];
        
        // Helper function to extract value from nested array structure
        const extractValue = (field) => {
          if (!field) return null;
          if (Array.isArray(field)) {
            if (field.length > 0) {
              const fieldValue = field[0];
              if (typeof fieldValue === 'object' && fieldValue.value !== undefined) {
                return fieldValue.value;
              } else {
                return String(fieldValue);
              }
            }
          } else {
            return String(field);
          }
          return null;
        };
        
        // Name section
        const name = extractValue(value.name);
        if (name && name !== 'not available') {
          sections.push(`Name: ${name}`);
        } else {
          sections.push('Name: Not available');
        }
        
        // Background section
        const background = extractValue(value.background);
        if (background && background !== 'not available') {
          sections.push(`Background: ${background}`);
        } else {
          sections.push('Background: Not available');
        }
        
        // Emails section
        const emails = extractValue(value.emails);
        if (emails && emails !== 'not available') {
          sections.push(`Emails: ${emails}`);
        } else {
          sections.push('Emails: Not available');
        }
        
        // Phones section
        const phones = extractValue(value.phones);
        if (phones && phones !== 'not available') {
          sections.push(`Phones: ${phones}`);
        } else {
          sections.push('Phones: Not available');
        }
        
        // News section
        const news = extractValue(value.news);
        if (news && news !== 'not available') {
          sections.push(`News: ${news}`);
        } else {
          sections.push('News: Not available');
        }
        
        // Additional fields that might exist
        const title = extractValue(value.title);
        if (title && title !== 'not available') {
          sections.push(`Title: ${title}`);
        }
        
        const role = extractValue(value.role);
        if (role && role !== 'not available') {
          sections.push(`Role: ${role}`);
        }
        
        const position = extractValue(value.position);
        if (position && position !== 'not available') {
          sections.push(`Position: ${position}`);
        }
        
        if (sections.length > 0) {
          return sections.join('\n');
        }
        
        return JSON.stringify(value, null, 2);
      }
      
      return String(value);
    } catch (error) {
      console.error('Error in formatFounders:', error);
      return 'Error displaying founders data';
    }
  };

  // Format key_people data (new structured format with position, email, phone, fax, background)
  const formatKeyPeople = (value) => {
    try {
      if (!value) return 'Not available';
      
      if (Array.isArray(value)) {
        if (value.length === 0) return 'Not available';
        
        const peopleInfo = value.map((person, index) => {
          if (typeof person === 'object' && person !== null) {
            const sections = [];
            
            // Helper function to extract value from nested array structure
            const extractValue = (field) => {
              if (!field) return null;
              if (Array.isArray(field)) {
                if (field.length > 0) {
                  const fieldValue = field[0];
                  if (typeof fieldValue === 'object' && fieldValue.value !== undefined) {
                    return fieldValue.value;
                  } else {
                    return String(fieldValue);
                  }
                }
              } else if (typeof field === 'string') {
                return field;
              }
              return null;
            };
            
            // Name
            const name = extractValue(person.name);
            if (name && name.toLowerCase() !== 'not available') {
              sections.push(`👤 ${name}`);
            }
            
            // Position
            const position = extractValue(person.position);
            if (position && position.toLowerCase() !== 'not available') {
              sections.push(`   Position: ${position}`);
            }
            
            // Email
            const email = extractValue(person.email);
            if (email && email.toLowerCase() !== 'not available') {
              sections.push(`   Email: ${email}`);
            }
            
            // Phone
            const phone = extractValue(person.phone);
            if (phone && phone.toLowerCase() !== 'not available') {
              sections.push(`   Phone: ${phone}`);
            }
            
            // Fax
            const fax = extractValue(person.fax);
            if (fax && fax.toLowerCase() !== 'not available') {
              sections.push(`   Fax: ${fax}`);
            }
            
            // Background
            const background = extractValue(person.background);
            if (background && background.toLowerCase() !== 'not available') {
              sections.push(`   Background: ${background}`);
            }
            
            if (sections.length > 0) {
              return sections.join('\n');
            }
            
            return JSON.stringify(person, null, 2);
          }
          return String(person);
        });
        
        return peopleInfo.join('\n\n');
      }
      
      return String(value);
    } catch (error) {
      console.error('Error in formatKeyPeople:', error);
      return 'Error displaying key people data';
    }
  };

  // Format BCA Licenses data
  const formatBCALicenses = (licenses) => {
    if (!licenses || licenses.length === 0) return 'No BCA licenses found';
    
    const extractValue = (field) => {
      if (!field || field.length === 0) return 'N/A';
      return field[0]?.value || 'N/A';
    };
    
    return licenses.map((license, idx) => {
      const parts = [
        `License ${idx + 1}:`,
        `  Category: ${extractValue(license.category)}`,
        `  Sub-category: ${extractValue(license.sub_category)}`,
        `  Workhead: ${extractValue(license.workhead_code)} - ${extractValue(license.workhead_description)}`,
        `  Grade: ${extractValue(license.grade)}`,
        `  Tendering Limit: ${extractValue(license.tendering_limit)}`,
        `  Expiry: ${extractValue(license.expiry_date)}`
      ];
      return parts.join('\n');
    }).join('\n\n');
  };

  // Format Shareholding Structure data
  const formatShareholding = (shareholding) => {
    if (!shareholding || shareholding.length === 0) return 'No shareholding data found';
    
    const extractValue = (field) => {
      if (!field || field.length === 0) return 'N/A';
      return field[0]?.value || 'N/A';
    };
    
    return shareholding.map((share) => {
      return `${extractValue(share.share_type)} Shares: ${extractValue(share.share_quantity)} ${extractValue(share.currency)}`;
    }).join('\n');
  };

  // Format Employee Change History data
  const formatEmployeeHistory = (history) => {
    if (!history || history.length === 0) return 'No employee change history';
    
    const extractValue = (field) => {
      if (!field || field.length === 0) return 'N/A';
      return field[0]?.value || 'N/A';
    };
    
    return history.map((change) => {
      return `${extractValue(change.date)}: ${extractValue(change.previous_value)} → ${extractValue(change.new_value)} employees`;
    }).join('\n');
  };

  // Helper function to check if field has data
  const hasData = (field) => field && Array.isArray(field) && field.length > 0;

  // Format UEN data as structured list
  const formatUEN = (value) => {
    try {
      if (!value) return <Typography variant="body2" color="text.secondary">Not available</Typography>;
      
      if (Array.isArray(value)) {
        if (value.length === 0) return <Typography variant="body2" color="text.secondary">Not available</Typography>;
        
        // Filter out "not available" entries and format valid UENs
        const validUENs = value
          .map((item) => {
            if (typeof item === 'object' && item !== null && item.value !== undefined) {
              return { value: item.value, source: item.source || null };
            }
            if (typeof item === 'string') {
              return { value: item, source: null };
            }
            return null;
          })
          .filter((item) => item && item.value && item.value.toLowerCase() !== 'not available');
        
        if (validUENs.length === 0) {
          return <Typography variant="body2" color="text.secondary">Not available</Typography>;
        }
        
        // Extract source name from URL for cleaner display
        const getSourceName = (source) => {
          if (!source) return null;
          try {
            const url = new URL(source);
            const hostname = url.hostname.replace('www.', '');
            // Map common hostnames to friendly names
            if (hostname.includes('recordowl')) return 'RecordOwl';
            if (hostname.includes('companies.sg')) return 'Companies.sg';
            if (hostname.includes('acra')) return 'ACRA';
            return hostname;
          } catch {
            return source;
          }
        };
        
        return (
          <Box>
            {validUENs.map((item, idx) => (
              <Box key={idx} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 'medium' }}>
                  • {item.value}
                </Typography>
                {item.source && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    (Source: {getSourceName(item.source)})
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        );
      }
      
      if (typeof value === 'object' && value !== null && value.value !== undefined) {
        if (value.value.toLowerCase() === 'not available') {
          return <Typography variant="body2" color="text.secondary">Not available</Typography>;
        }
        return (
          <Box>
            <Typography variant="body2" component="span" sx={{ fontWeight: 'medium' }}>
              • {value.value}
            </Typography>
            {value.source && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (Source: {value.source})
              </Typography>
            )}
          </Box>
        );
      }
      
      return <Typography variant="body2">{String(value)}</Typography>;
    } catch (error) {
      console.error('Error in formatUEN:', error);
      return <Typography variant="body2" color="error">Error displaying UEN data</Typography>;
    }
  };

  // Helper to parse website accessibility information
  const parseWebsiteAccessibility = (websiteArray) => {
    if (!websiteArray || !Array.isArray(websiteArray) || websiteArray.length === 0) {
      return { status: 'unknown', url: null, lastChecked: null, outcome: null };
    }
    
    const website = websiteArray[0];
    return {
      status: website.accessible === true ? 'accessible' : 
              website.accessible === false ? 'inaccessible' : 'unknown',
      url: website.value || null,
      lastChecked: website.last_checked || null,
      outcome: website.validation_outcome || null
    };
  };

  // RecordOwl status helpers
  const getRecordOwlStatusLabel = (status) => {
    const labels = {
      not_listed: 'Not listed on RecordOwl',
      accessible: 'Accessible',
      not_found: 'Page not found (404)',
      temporarily_unavailable: 'Temporarily unavailable',
      error: 'Error',
      pending: 'Pending'
    };
    return labels[status] || status || 'Unknown';
  };

  const getRecordOwlStatusColor = (status) => {
    const colors = {
      not_listed: 'default',
      accessible: 'success',
      not_found: 'error',
      temporarily_unavailable: 'warning',
      error: 'error',
      pending: 'default'
    };
    return colors[status] || 'default';
  };

  // Website scrape status helpers
  const getWebsiteScrapeStatusLabel = (status) => {
    const labels = {
      no_website: 'No website found',
      scraped: 'Scraped',
      timeout: 'Scrape timeout',
      blocked: 'Website blocked scraper',
      api_error: 'Scrape API error',
      page_not_found: 'Page not found',
      error: 'Scrape failed',
      pending: 'Pending',
      skipped: 'Skipped'
    };
    return labels[status] || status || 'Unknown';
  };

  const getWebsiteScrapeStatusColor = (status) => {
    const colors = {
      no_website: 'default',
      scraped: 'success',
      timeout: 'warning',
      blocked: 'warning',
      api_error: 'warning',
      page_not_found: 'error',
      error: 'error',
      pending: 'default',
      skipped: 'default'
    };
    return colors[status] || 'default';
  };

  // Helper to render value+source, with clickable URLs/emails
  const renderValueWithSource = (items) => {
    if (!items || items.length === 0) return 'Not available';
    return items.map((item, idx) => {
      let valueElem = null;
      if (typeof item.value === 'string') {
        if (item.value.match(/^https?:\/\//)) {
          // URL
          valueElem = (
            <Link href={item.value} target="_blank" rel="noopener noreferrer">
              {item.value} <OpenInNewIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
            </Link>
          );
        } else if (item.value.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) {
          // Email
          valueElem = (
            <Link href={`mailto:${item.value}`}>{item.value}</Link>
          );
        } else {
          valueElem = item.value;
        }
      } else {
        valueElem = String(item.value);
      }
      return (
        <div key={idx} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{valueElem}</span>
          {item.source && typeof item.source === 'string' && item.source.trim() !== '' && (
            <Link
              href={item.source}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ml: 1, display: 'inline-flex', alignItems: 'center' }}
              title="Source"
            >
              <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
            </Link>
          )}
        </div>
      );
    });
  };

  // Build filter params for export (applies current filters)
  const getExportFilterParams = () => ({
    ...(leadsStatusFilter !== 'all' && { status: leadsStatusFilter }),
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(leadsEnrichmentFilter !== 'all' && { enrichment_status: leadsEnrichmentFilter }),
    ...(leadsSourceFilter !== 'all' && { source: leadsSourceFilter }),
  });

  const handleExportExcel = async () => {
    try {
      const params = getExportFilterParams();
      const response = await jobsAPI.exportJobLeadsExcel(id, params);
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `job_${id}_leads_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = getExportFilterParams();
      const response = await jobsAPI.exportJobLeadsCSV(id, params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `job_${id}_leads_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  // Handler for Custom Export (opens dialog with all filtered leads)
  const handleOpenCustomExport = async () => {
    setLoadingFilteredExport(true);
    try {
      // Fetch all lead IDs that match current filters
      const params = getExportFilterParams();
      const result = await jobsAPI.getAllLeadIds(id, params);
      setFilteredExportLeadIds(result.lead_ids || []);
      setFilteredExportOpen(true);
    } catch (error) {
      console.error('Failed to fetch lead IDs:', error);
      alert('Failed to open custom export. Please try again.');
    } finally {
      setLoadingFilteredExport(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status ? status.toLowerCase() : '') {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'warning';
      case 'pending':
        return 'default';
      case 'rerun':
        return 'info';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load job details
      </Alert>
    );
  }

  if (!job) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Job not found
      </Alert>
    );
  }

  // Calculate counts for tabs
  const leadsTabCount = leadsCount?.total || 0;
  const gebizTabCount = gebizSummary?.total || 0;

  return (
    <Box>
      {/* ============================================ */}
      {/* NEW HEADER DESIGN */}
      {/* ============================================ */}
      
      {/* Back to Jobs Link */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/jobs')}
        sx={{ mb: 2 }}
        size="small"
        >
          Back to Jobs
        </Button>

      {/* Meta Row: ID, Status, Type, Date */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            #{job.id}
          </Typography>
          <StatusChip status={job.status} />
          <TypeChip jobType={job.job_type} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          <CalendarIcon sx={{ fontSize: 16 }} />
          <Typography variant="body2">
            {new Date(job.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })} at {new Date(job.created_at).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })}
          </Typography>
        </Box>
      </Box>

      {/* Job Title */}
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {job.name}
        </Typography>

      {/* Job Description */}
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {job.description || 'No description provided'}
      </Typography>

      {/* ============================================ */}
      {/* TABS */}
      {/* ============================================ */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
            },
          }}
        >
          <Tab 
            icon={<DescriptionIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Overview" 
          />
          <Tab 
            icon={<AssignmentIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Leads
                {leadsTabCount > 0 && (
                  <Chip label={leadsTabCount} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
                )}
              </Box>
            }
          />
          <Tab 
            icon={<AwardsIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                GeBIZ Awards
                {gebizTabCount > 0 && (
                  <Chip label={gebizTabCount} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* ============================================ */}
      {/* TAB 0: OVERVIEW */}
      {/* ============================================ */}
      <TabPanel value={activeTab} index={0}>
        {/* Task Progress moved to GeBIZ Awards section for cleaner UX */}
        
        {/* Summary Cards Row - Uses stats from leads_enriched table */}
        {(() => {
          // Use jobStats API for accurate counts (from leads_enriched table)
          const totalLeads = jobStats?.total_leads || 0;
          const completed = jobStats?.completed_leads || 0;
          const skipped = jobStats?.skipped_leads || 0;
          const failed = jobStats?.failed_leads || 0;
          const pending = jobStats?.pending_leads || 0;
          const processing = jobStats?.processing_leads || 0;
          const avgQuality = jobStats?.average_quality_score || 0;

          const summaryCards = [
            {
              title: 'Total Leads',
              value: totalLeads,
              icon: <TotalLeadsIcon />,
              color: '#1976d2',
            },
            {
              title: 'Completed',
              value: completed,
              icon: <CheckCircleIcon />,
              color: '#2e7d32',
            },
            {
              title: 'Skipped',
              value: skipped,
              icon: <SkipIcon />,
              color: skipped > 0 ? '#ed6c02' : '#9e9e9e',
            },
            {
              title: 'Failed',
              value: failed,
              icon: <ErrorIcon />,
              color: failed > 0 ? '#d32f2f' : '#9e9e9e',
            },
            {
              title: 'Pending',
              value: pending,
              icon: <PendingIcon />,
              color: pending > 0 ? '#0288d1' : '#9e9e9e',
            },
            {
              title: 'Processing',
              value: processing,
              icon: <ProcessingIcon />,
              color: processing > 0 ? '#7b1fa2' : '#9e9e9e',
            },
            {
              title: 'Avg. Quality',
              value: `${avgQuality}%`,
              icon: <AssignmentIcon />,
              color: avgQuality >= 80 ? '#2e7d32' : avgQuality >= 60 ? '#ed6c02' : '#d32f2f',
            },
          ];

          return (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {summaryCards.map((card, index) => (
                <Grid item xs={6} sm={4} md={12/7} key={index}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {card.title}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: card.color, lineHeight: 1.2 }}>
                            {card.value}
                          </Typography>
                          {card.subtitle && (
                            <Typography variant="caption" color="text.secondary">
                              {card.subtitle}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ color: card.color, opacity: 0.6 }}>
                          {card.icon}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          );
        })()}

        {/* SGPBusiness Alert List: source breakdown + scrape/match actions */}
        <SGPBusinessOverviewCard jobId={id} jobStats={jobStats} />

        {/* Processing Progress Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Processing Progress
            </Typography>
            {(() => {
              // Use jobStats for accurate progress (from leads_enriched table)
              const totalLeads = jobStats?.total_leads || 0;
              const completed = jobStats?.completed_leads || 0;
              const skipped = jobStats?.skipped_leads || 0;
              const processed = completed + skipped; // Completed + Skipped = Processed
              const progressPercent = jobStats?.completion_percentage || 0;
              
              return (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {processed} of {totalLeads} leads processed ({completed} completed, {skipped} skipped)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {Math.round(progressPercent)}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={progressPercent}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        backgroundColor: progressPercent >= 100 ? '#2e7d32' : '#1976d2',
                      }
                    }}
                  />
                </>
              );
            })()}
          </CardContent>
        </Card>

        {/* Job Configuration Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Job Configuration
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {job.status?.toLowerCase() === 'completed' ? (
                <>
                  <CheckCircleIcon color="success" />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Completed
                    </Typography>
                    {job.completed_at && (
                      <Typography variant="body2" color="text.secondary">
                        {new Date(job.completed_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })} at {new Date(job.completed_at).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })}
                      </Typography>
                    )}
                  </Box>
                </>
              ) : job.status?.toLowerCase() === 'processing' ? (
                <>
                  <ScheduleIcon color="warning" />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Processing
                    </Typography>
                    {job.started_at && (
                      <Typography variant="body2" color="text.secondary">
                        Started: {new Date(job.started_at).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <PendingIcon color="disabled" />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {job.status || 'Pending'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Created: {new Date(job.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Action Buttons Row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {job.status === 'pending' && (
            <Button
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={handleStartJob}
              disabled={startJobMutation.isLoading}
            >
              Start Job
            </Button>
          )}
          {((leads && leads.length > 0) || (allLeads && allLeads.length > 0)) && (
            <>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
              >
                Export Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteJob}
          >
            Delete Job
          </Button>
      </Box>

        {/* Error Message Card */}
        {job.error_message && (
          <Card sx={{ mb: 3, borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="error">
                Error Details
              </Typography>
              <Typography variant="body1" color="error">
                {job.error_message}
                  </Typography>
            </CardContent>
          </Card>
        )}

        {/* Source File Card */}
          {job.source_file_name && (
          <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Source File
                </Typography>
                <Typography variant="body1">
                  {job.source_file_name}
                </Typography>
              {job.source_file_size && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Size: {(job.source_file_size / 1024).toFixed(2)} KB
                </Typography>
              )}
              </CardContent>
            </Card>
          )}

        {/* API Usage Logs */}
        {usageLogs && usageLogs.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                API Usage Logs
              </Typography>
              <List dense>
                {usageLogs.slice(0, 10).map((log) => (
                  <ListItem key={log.id}>
                  <ListItemText
                      primary={`${log.api_provider} - ${log.request_type}`}
                      secondary={`${log.company_name || 'N/A'} | Cost: $${(log.cost_cents / 100).toFixed(4)} | ${new Date(log.created_at).toLocaleString()}`}
                  />
                </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      {/* ============================================ */}
      {/* TAB 1: LEADS */}
      {/* ============================================ */}
      <TabPanel value={activeTab} index={1}>
        {/* Browserless Session Panel - for batch enrichment */}
        <EnrichmentSessionPanel
          jobId={parseInt(id, 10)}
        />
        
        {/* Task Progress for Enrichment */}
        <TaskProgress
          taskId={currentTaskId}  // Direct task tracking - no race condition!
          jobId={id}  // Fallback for finding active tasks
          pollingInterval={2000}
          onTaskComplete={(task) => {
            queryClient.invalidateQueries(['jobLeads', id]);
            queryClient.invalidateQueries(['allJobLeads', id]);
            queryClient.invalidateQueries(['jobLeadsCount', id]);
            // Clear enriching lead ID when task completes
            setEnrichingLeadId(null);
            // Clear the current task ID after completion (optional: keep to show final status)
            // setCurrentTaskId(null);
          }}
          showWhenEmpty={!!currentTaskId}  // Show if we have a task to track
        />
        
        <LeadsTable
          leads={leads || []}
          totalCount={leadsCount?.total || 0}
          onViewDetails={handleLeadClick}
          onViewAwards={handleViewAwards}
          onViewNotes={handleViewNotes}
          onExportCSV={handleExportCSV}
          onExportExcel={handleExportExcel}
          onCustomExport={handleOpenCustomExport}
          customExportLoading={loadingFilteredExport}
          onEnrichSelected={() => {
            // Open enrichment dialog (backend API is per-lead, enrich first selected)
            if (selectedLeads.length > 0) {
              setEnrichDialogOpen(true);
            }
          }}
          enrichLoading={enrichmentMutation.isLoading}
          loading={leadsLoading}
          selectedLeads={selectedLeads}
          onToggleSelect={handleToggleLeadSelection}
          onSelectAll={(checked, leadIds) => {
            if (checked) {
              setSelectedLeads(leadIds);
            } else {
              setSelectedLeads([]);
            }
          }}
          // Pagination props
          page={page - 1}  // MUI TablePagination is 0-indexed, our state is 1-indexed
          rowsPerPage={pageSize}
          onPageChange={(event, newPage) => setPage(newPage + 1)}
          onRowsPerPageChange={(event) => {
            setPageSize(parseInt(event.target.value, 10));
            setPage(1);  // Reset to first page when changing page size
          }}
          // Filter props (server-side filtering)
          searchQuery={leadsSearchQuery}
          onSearchChange={setLeadsSearchQuery}
          statusFilter={leadsStatusFilter}
          onStatusFilterChange={setLeadsStatusFilter}
          enrichmentFilter={leadsEnrichmentFilter}
          onEnrichmentFilterChange={setLeadsEnrichmentFilter}
          sourceFilter={leadsSourceFilter}
          onSourceFilterChange={setLeadsSourceFilter}
          // Skip/Unskip props
          onSkipLead={handleSkipLead}
          onUnskipLead={handleUnskipLead}
          onSkipSelectedLeads={handleSkipSelectedLeads}
          onUnskipSelectedLeads={handleUnskipSelectedLeads}
          skipLoading={skipLoading}
        />
      </TabPanel>

      {/* ============================================ */}
      {/* TAB 2: GEBIZ AWARDS */}
      {/* ============================================ */}
      <TabPanel value={activeTab} index={2}>
        {/* Action Buttons */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Processing Actions
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PdfIcon />}
                onClick={() => setPdfDialogOpen(true)}
                disabled={extractionMutation.isLoading || matchingMutation.isLoading}
              >
                Extract from PDFs
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={matchingMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
                onClick={() => matchingMutation.mutate()}
                disabled={extractionMutation.isLoading || matchingMutation.isLoading}
              >
                {matchingMutation.isLoading ? 'Starting...' : 'Match Companies'}
              </Button>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Extract: Parse PDF files to extract award data • Match: Link extracted companies to existing leads
          </Typography>
        </Paper>
        
        {/* Task Progress - shows when a task is running */}
        <TaskProgress
          taskId={currentTaskId}  // Direct task tracking - no race condition!
          jobId={id}  // Fallback for finding active tasks
          pollingInterval={2000}
          onTaskComplete={(task) => {
            queryClient.invalidateQueries(['gebizAwards', id]);
            queryClient.invalidateQueries(['gebizAwardsCount', id]);
            queryClient.invalidateQueries(['gebizSummary', id]);
            queryClient.invalidateQueries(['jobLeads', id]);
            // Clear enriching lead ID when task completes
            setEnrichingLeadId(null);
            // Clear the current task ID after completion (optional: keep to show final status)
            // setCurrentTaskId(null);
          }}
          showWhenEmpty={!!currentTaskId}  // Show if we have a task to track
        />
        
        <GeBIZSummary 
          jobId={id} 
          onFilterChange={(filter) => setGebizStatusFilter(filter)}
        />
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          GeBIZ Awards
        </Typography>
        
        <GeBIZAwardsTable
          // Data props (from parent API call)
          awards={gebizAwards || []}
          loading={gebizAwardsLoading}
          error={gebizAwardsError}
          // Pagination props (controlled by parent)
          page={gebizPage}
          rowsPerPage={gebizRowsPerPage}
          onPageChange={(event, newPage) => setGebizPage(newPage)}
          onRowsPerPageChange={(event) => {
            setGebizRowsPerPage(parseInt(event.target.value, 10));
            setGebizPage(0);
          }}
          // Search props (immediate value for TextField display)
          searchQuery={gebizSearchQuery}
          onSearchChange={setGebizSearchQuery}
          // Other props
          matchStatusFilter={gebizStatusFilter}
          onFilterChange={setGebizStatusFilter}
          fuzzyReviewCount={gebizSummary?.fuzzy_review ?? 0}
          totalCount={gebizAwardsCount?.total ?? 0}
          onRefresh={handleGebizRefresh}
          onReviewClick={(award) => {
            setSelectedAward(award);
            setReviewDialogOpen(true);
          }}
        />
      </TabPanel>

      {/* ============================================ */}
      {/* DIALOGS */}
      {/* ============================================ */}

      {/* PDF Selection Dialog */}
      <PDFSelectionDialog
        open={pdfDialogOpen}
        onClose={() => setPdfDialogOpen(false)}
        jobId={id}
        onExtract={(files) => extractionMutation.mutate(files)}
        extractLoading={extractionMutation.isLoading}
      />

      {/* Fuzzy Review Dialog */}
      <FuzzyReviewDialog
        open={reviewDialogOpen}
        onClose={() => {
          setReviewDialogOpen(false);
          setSelectedAward(null);
        }}
        award={selectedAward}
        jobId={id}
      />

      {/* Lead Awards Dialog */}
      <LeadAwardsDialog
        open={awardsDialogOpen}
        onClose={() => {
          setAwardsDialogOpen(false);
          setSelectedLeadForAwards(null);
        }}
        jobId={parseInt(id, 10)}
        lead={selectedLeadForAwards}
      />

      {/* Lead Notes Dialog */}
      <LeadNotesDialog
        open={notesDialogOpen}
        onClose={() => {
          setNotesDialogOpen(false);
          setSelectedLeadForNotes(null);
        }}
        jobId={parseInt(id, 10)}
        lead={selectedLeadForNotes}
      />

      {/* Enrichment Confirmation Dialog */}
      <Dialog
        open={enrichDialogOpen}
        onClose={() => setEnrichDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Enrich Lead</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Lead info */}
            {selectedLeads.length > 0 && leads && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Selected Lead:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {leads.find(l => l.id === selectedLeads[0])?.company_name || `Lead #${selectedLeads[0]}`}
                </Typography>
              </Box>
            )}
            
            {/* Authentication Status Card */}
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        RecordOwl Authentication
                      </Typography>
                      {hasValidAuth && (
                        <Typography variant="caption" sx={{ 
                          bgcolor: 'success.lighter', 
                          color: 'success.dark',
                          px: 0.75, 
                          py: 0.25, 
                          borderRadius: 0.5,
                          fontSize: '0.65rem'
                        }}>
                          ✓ Ready
                        </Typography>
                      )}
                    </Box>
                    {/* Authentication Status - Priority: Active Session > Redis Cookies > None */}
                    {(browserlessLoading || redisCookiesLoading) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Checking authentication...</Typography>
                      </Box>
                    ) : hasBrowserlessSession ? (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                          <Typography variant="body2" color="success.main">
                            Active session ({browserlessSession?.pages_scraped || 0} pages scraped)
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                          Cloud browser connected and ready for scraping
                        </Typography>
                      </Box>
                    ) : hasRedisCookies ? (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                          <Typography variant="body2" color="success.main">
                            Cookies available ({redisCookies?.expires_in_minutes || '?'} min remaining)
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                          Session expired, using saved cookies for RecordOwl scraping
                        </Typography>
                      </Box>
                    ) : browserlessSession?.status === 'awaiting_login' ? (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <ScheduleIcon color="warning" fontSize="small" />
                          <Typography variant="body2" color="warning.main">
                            Session awaiting login
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Complete Google OAuth in the LiveURL, then click "Verify Login"
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <ErrorIcon color="warning" fontSize="small" />
                          <Typography variant="body2" color="warning.main">
                            No authentication available
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Start a Browserless session from the Leads tab, or check "Skip RecordOwl Auth"
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
            
            {/* Skip checkbox */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 1,
                p: 1.5,
                bgcolor: skipRecordOwlAuth ? 'warning.lighter' : 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: skipRecordOwlAuth ? 'warning.light' : 'grey.200'
              }}
            >
              <Checkbox
                checked={skipRecordOwlAuth}
                onChange={(e) => setSkipRecordOwlAuth(e.target.checked)}
                sx={{ p: 0, mt: 0.25 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={skipRecordOwlAuth ? 'bold' : 'normal'}>
                  Skip RecordOwl Auth (use fallback only)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {skipRecordOwlAuth 
                    ? 'Will skip authenticated RecordOwl scraping. Less data may be available.'
                    : 'Uncheck to use authenticated RecordOwl scraping for richer data.'}
                </Typography>
              </Box>
            </Box>
            
            {/* Warning if no authentication available and not skipping */}
            {!hasValidAuth && !skipRecordOwlAuth && !browserlessLoading && !redisCookiesLoading && (
              <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
                No active session or saved cookies. Start a Browserless session from the Leads tab, or check "Skip RecordOwl Auth" to use web search fallback only.
              </Alert>
            )}
            
            {/* Info about Redis cookies if available but no active session */}
            {hasRedisCookies && !hasBrowserlessSession && !skipRecordOwlAuth && (
              <Alert severity="success" sx={{ fontSize: '0.875rem' }}>
                ✓ Session expired, using saved cookies ({redisCookies?.expires_in_minutes || '?'} min remaining). 
                Ready for enrichment!
              </Alert>
            )}
            
            {/* Error if session check failed */}
            {browserlessError && !skipRecordOwlAuth && (
              <Alert 
                severity="info" 
                sx={{ fontSize: '0.875rem' }}
                action={
                  <Button color="inherit" size="small" onClick={() => { refetchBrowserlessSession(); refetchRedisCookies(); }}>
                    Retry
                  </Button>
                }
              >
                Could not check authentication status. Start a new session from the Leads tab if needed.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrichDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              if (selectedLeads.length > 0) {
                enrichmentMutation.mutate({ leadId: selectedLeads[0], skipAuth: skipRecordOwlAuth });
              }
            }}
            disabled={enrichmentMutation.isLoading}
            startIcon={enrichmentMutation.isLoading ? <CircularProgress size={16} /> : null}
          >
            {enrichmentMutation.isLoading ? 'Starting...' : 'Start Enrichment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog
        open={leadDetailOpen}
        onClose={handleCloseLeadDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {selectedLead?.company_name}
            </Typography>
            <IconButton onClick={handleCloseLeadDetail}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailedLead ? (
            <Stack spacing={3}>

              {/* Status and Quality */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Chip
                  label={detailedLead.status.toUpperCase()}
                  color={getStatusColor(detailedLead.status)}
                />
                <Chip
                  label={`${detailedLead.data_quality_score}% Quality`}
                  color={detailedLead.data_quality_score >= 80 ? 'success' : detailedLead.data_quality_score >= 60 ? 'warning' : 'error'}
                />
                <Typography variant="body2" color="textSecondary">
                  Fields: {detailedLead.fields_completed}/{detailedLead.total_fields}
                </Typography>
              </Box>

              {/* Flags Section */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Flags
                  </Typography>
                  <LeadFlags jobId={id} leadId={detailedLead.id} />
                </CardContent>
              </Card>

              {/* Company Website */}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LanguageIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Company Website</Typography>
                  </Box>
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {renderValueWithSource(Array.isArray(detailedLead.company_website) ? detailedLead.company_website : [])}
                  </div>

                  {(() => {
                    const accessibility = parseWebsiteAccessibility(detailedLead.company_website);
                    const scrapeStatus = detailedLead.website_scrape_status;
                    const showScrapeChip = scrapeStatus && !['scraped', 'no_website', 'pending', 'skipped'].includes(scrapeStatus);

                    // Only show status info if we have a website URL or a scrape status
                    if (!accessibility.url && !showScrapeChip) return null;

                    return (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {accessibility.url && (
                          <Chip
                            label={
                              accessibility.status === 'accessible' ? 'Accessible' :
                              accessibility.status === 'inaccessible' ? 'Inaccessible' : 'Unknown Status'
                            }
                            color={
                              accessibility.status === 'accessible' ? 'success' :
                              accessibility.status === 'inaccessible' ? 'error' : 'default'
                            }
                            size="small"
                            icon={
                              accessibility.status === 'accessible' ? <CheckCircleIcon /> :
                              accessibility.status === 'inaccessible' ? <ErrorIcon /> : null
                            }
                          />
                        )}
                        {showScrapeChip && (
                          <Chip
                            label={getWebsiteScrapeStatusLabel(scrapeStatus)}
                            color={getWebsiteScrapeStatusColor(scrapeStatus)}
                            size="small"
                            icon={
                              ['page_not_found', 'error'].includes(scrapeStatus) ? <ErrorIcon /> : null
                            }
                          />
                        )}
                        {accessibility.lastChecked && (
                          <Typography variant="body2" color="textSecondary">
                            Checked: {new Date(accessibility.lastChecked).toLocaleString()}
                          </Typography>
                        )}
                        {accessibility.outcome && accessibility.outcome !== accessibility.status && (
                          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            ({accessibility.outcome})
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Business Information */}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Business Information</Typography>
                  </Box>
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {renderValueWithSource(Array.isArray(detailedLead.business_info) ? detailedLead.business_info : [])}
                  </div>
                </CardContent>
              </Card>

              {/* Background */}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ArticleIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Background</Typography>
                  </Box>
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {renderValueWithSource(Array.isArray(detailedLead.background) ? detailedLead.background : [])}
                  </div>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6">Emails</Typography>
                      </Box>
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {renderValueWithSource(Array.isArray(detailedLead.emails) ? detailedLead.emails : [])}
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PhoneIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6">Phone Numbers</Typography>
                      </Box>
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {renderValueWithSource(Array.isArray(detailedLead.phones) ? detailedLead.phones : [])}
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* News */}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ArticleIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">News & Media Coverage</Typography>
                  </Box>
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {renderValueWithSource(Array.isArray(detailedLead.news) ? detailedLead.news : [])}
                  </div>
                </CardContent>
              </Card>

              {/* Key People (new format) */}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Key People</Typography>
                  </Box>
                  <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                    {detailedLead.key_people && detailedLead.key_people.length > 0 
                      ? formatKeyPeople(detailedLead.key_people)
                      : formatFounders(detailedLead.founders)}
                  </div>
                </CardContent>
              </Card>

              {/* UEN (Unique Entity Number) */}
              {(detailedLead.uen && detailedLead.uen.length > 0) && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">UEN (Unique Entity Number)</Typography>
                    </Box>
                    {formatUEN(detailedLead.uen)}
                  </CardContent>
                </Card>
              )}

              {/* RecordOwl Company Profile */}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">RecordOwl Company Profile</Typography>
                    </Box>
                    {detailedLead.recordowl_status && (
                      <Chip
                        label={getRecordOwlStatusLabel(detailedLead.recordowl_status)}
                        color={getRecordOwlStatusColor(detailedLead.recordowl_status)}
                        size="small"
                        icon={
                          detailedLead.recordowl_status === 'accessible' ? <CheckCircleIcon /> :
                          ['not_found', 'error'].includes(detailedLead.recordowl_status) ? <ErrorIcon /> : null
                        }
                      />
                    )}
                  </Box>
                  {detailedLead.recordowl_company_url ? (
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {renderValueWithSource(Array.isArray(detailedLead.recordowl_company_url)
                        ? detailedLead.recordowl_company_url
                        : [detailedLead.recordowl_company_url])}
                    </div>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {detailedLead.recordowl_status === 'not_listed'
                        ? 'This company was not found on RecordOwl.'
                        : detailedLead.recordowl_status === 'pending'
                        ? 'RecordOwl lookup has not been performed yet.'
                        : 'No RecordOwl profile URL available.'}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Company Overview (RecordOwl Data) */}
              {(hasData(detailedLead.registered_address) || 
                hasData(detailedLead.operating_status) || 
                hasData(detailedLead.company_age) || 
                hasData(detailedLead.building)) && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Company Overview</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      {hasData(detailedLead.registered_address) && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">Registered Address</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.registered_address)}
                          </div>
                        </Grid>
                      )}
                      {hasData(detailedLead.building) && (
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" color="textSecondary">Building</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.building)}
                          </div>
                        </Grid>
                      )}
                      {hasData(detailedLead.operating_status) && (
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" color="textSecondary">Operating Status</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.operating_status)}
                          </div>
                        </Grid>
                      )}
                      {hasData(detailedLead.company_age) && (
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" color="textSecondary">Company Age</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.company_age)}
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Industry Classification (RecordOwl Data) */}
              {(hasData(detailedLead.primary_ssic_code) || 
                hasData(detailedLead.primary_industry) || 
                hasData(detailedLead.secondary_ssic_code) || 
                hasData(detailedLead.secondary_industry)) && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Industry Classification</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>Primary Industry</Typography>
                        {hasData(detailedLead.primary_ssic_code) && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="textSecondary">SSIC Code</Typography>
                            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {renderValueWithSource(detailedLead.primary_ssic_code)}
                            </div>
                          </Box>
                        )}
                        {hasData(detailedLead.primary_industry) && (
                          <Box>
                            <Typography variant="body2" color="textSecondary">Industry Description</Typography>
                            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {renderValueWithSource(detailedLead.primary_industry)}
                            </div>
                          </Box>
                        )}
                      </Grid>
                      {(hasData(detailedLead.secondary_ssic_code) || hasData(detailedLead.secondary_industry)) && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="primary" gutterBottom>Secondary Industry</Typography>
                          {hasData(detailedLead.secondary_ssic_code) && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2" color="textSecondary">SSIC Code</Typography>
                              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {renderValueWithSource(detailedLead.secondary_ssic_code)}
                              </div>
                            </Box>
                          )}
                          {hasData(detailedLead.secondary_industry) && (
                            <Box>
                              <Typography variant="body2" color="textSecondary">Industry Description</Typography>
                              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {renderValueWithSource(detailedLead.secondary_industry)}
                              </div>
                            </Box>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Company Timeline (RecordOwl Data) */}
              {(hasData(detailedLead.company_founded_date) || 
                hasData(detailedLead.employee_count) ||
                hasData(detailedLead.employee_change_history)) && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Company Timeline</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      {hasData(detailedLead.company_founded_date) && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">Founded Date</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.company_founded_date)}
                          </div>
                        </Grid>
                      )}
                      {hasData(detailedLead.employee_count) && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">Current Employee Count</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.employee_count)}
                          </div>
                        </Grid>
                      )}
                      {hasData(detailedLead.employee_change_history) && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">Employee Change History</Typography>
                          <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                            {formatEmployeeHistory(detailedLead.employee_change_history)}
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Financial Information (RecordOwl Data) */}
              {(hasData(detailedLead.paid_up_capital) || 
                hasData(detailedLead.number_of_officers) ||
                hasData(detailedLead.fiscal_period) ||
                hasData(detailedLead.annual_return_date) ||
                hasData(detailedLead.auditor_name) ||
                hasData(detailedLead.auditor_address)) && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <FinanceIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Financial Information</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      {hasData(detailedLead.paid_up_capital) && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">Paid-up Capital</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.paid_up_capital)}
                          </div>
                        </Grid>
                      )}
                      {hasData(detailedLead.number_of_officers) && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">Number of Officers</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.number_of_officers)}
                          </div>
                        </Grid>
                      )}
                      {hasData(detailedLead.fiscal_period) && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">Fiscal Period</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.fiscal_period)}
                          </div>
                        </Grid>
                      )}
                      {hasData(detailedLead.annual_return_date) && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">Annual Return Date</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.annual_return_date)}
                          </div>
                        </Grid>
                      )}
                      {hasData(detailedLead.auditor_name) && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">Auditor Name</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.auditor_name)}
                          </div>
                        </Grid>
                      )}
                      {hasData(detailedLead.auditor_address) && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">Auditor Address</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.auditor_address)}
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Valuation Info (RecordOwl Data) */}
              {(hasData(detailedLead.ipo_status) || hasData(detailedLead.number_of_charges)) && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TrendingIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Valuation</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      {hasData(detailedLead.ipo_status) && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">IPO Status</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.ipo_status)}
                          </div>
                        </Grid>
                      )}
                      {hasData(detailedLead.number_of_charges) && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">Number of Charges</Typography>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.number_of_charges)}
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* BCA Licenses (RecordOwl Data) */}
              {hasData(detailedLead.bca_licenses) && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LicenseIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">BCA Licenses & Certifications</Typography>
                    </Box>
                    <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                      {formatBCALicenses(detailedLead.bca_licenses)}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Shareholding Structure (RecordOwl Data) */}
              {hasData(detailedLead.shareholding_structure) && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ShareholdingIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Shareholding Structure</Typography>
                    </Box>
                    <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                      {formatShareholding(detailedLead.shareholding_structure)}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Social Media (RecordOwl Data) */}
              {(hasData(detailedLead.facebook_url) || hasData(detailedLead.instagram_url)) && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LanguageIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Social Media</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      {hasData(detailedLead.facebook_url) && (
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <FacebookIcon sx={{ mr: 0.5, fontSize: 18, color: '#1877F2' }} />
                            <Typography variant="body2" color="textSecondary">Facebook</Typography>
                          </Box>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.facebook_url)}
                          </div>
                        </Grid>
                      )}
                      {hasData(detailedLead.instagram_url) && (
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <InstagramIcon sx={{ mr: 0.5, fontSize: 18, color: '#E4405F' }} />
                            <Typography variant="body2" color="textSecondary">Instagram</Typography>
                          </Box>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {renderValueWithSource(detailedLead.instagram_url)}
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Processing Details */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Processing Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Created</Typography>
                      <Typography variant="body1">
                        {detailedLead.created_at ? new Date(detailedLead.created_at).toLocaleString() : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Processed</Typography>
                      <Typography variant="body1">
                        {detailedLead.processed_at ? new Date(detailedLead.processed_at).toLocaleString() : 'Pending'}
                      </Typography>
                    </Grid>
                  </Grid>
                  {detailedLead.processing_errors && detailedLead.processing_errors.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="error" gutterBottom>Processing Errors:</Typography>
                      <List dense>
                        {detailedLead.processing_errors.map((error, index) => (
                          <ListItem key={index}>
                            <ListItemText primary={error} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Other Citations & Search Results */}
              {detailedLead.other_citations_search_results && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ArticleIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Other Citations & Search Results</Typography>
                    </Box>
                    {Object.entries(detailedLead.other_citations_search_results).map(([sourceKey, sourceVal]) => (
                      <Box key={sourceKey} sx={{ mb: 2, pl: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize' }}>
                          {sourceKey.replace(/_/g, ' ')}
                        </Typography>
                        
                        {/* Handle string values (new workflow format) - split by pipe for readability */}
                        {typeof sourceVal === 'string' && (
                          <Box>
                            {sourceVal.split(' | ').map((segment, idx) => (
                              <Typography 
                                key={idx} 
                                variant="body2" 
                                sx={{ 
                                  mb: 1, 
                                  wordBreak: 'break-word',
                                  '&:last-child': { mb: 0 }
                                }}
                              >
                                {segment.trim()}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        
                        {/* Handle object values (old workflow format) */}
                        {typeof sourceVal === 'object' && sourceVal !== null && (
                          <>
                            {sourceVal.citations && sourceVal.citations.length > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Citations:</Typography>
                                {sourceVal.citations.map((url, idx) => (
                                  <div key={idx}>
                                    <Link href={url} target="_blank" rel="noopener noreferrer">
                                      {url} <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                                    </Link>
                                  </div>
                                ))}
                              </Box>
                            )}
                            {sourceVal.search_results && sourceVal.search_results.length > 0 && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Search Results:</Typography>
                                {sourceVal.search_results.map((result, idx) => (
                                  <div key={idx} style={{ marginBottom: 4 }}>
                                    <Link href={result.url} target="_blank" rel="noopener noreferrer">
                                      {result.title || result.url} <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                                    </Link>
                                    {result.date && (
                                      <span style={{ marginLeft: 8, color: '#888' }}>Date: {result.date}</span>
                                    )}
                                    {result.last_updated && (
                                      <span style={{ marginLeft: 8, color: '#888' }}>Last Updated: {result.last_updated}</span>
                                    )}
                                  </div>
                                ))}
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              )}
            </Stack>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => setSingleLeadExportOpen(true)}
          >
            Export Selected Fields
          </Button>
          <Button onClick={handleCloseLeadDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Single Lead Export Dialog */}
      {singleLeadExportOpen && selectedLead && (
        <CustomExportDialog
          open={singleLeadExportOpen}
          onClose={() => setSingleLeadExportOpen(false)}
          jobId={id}
          singleLeadId={selectedLead.id}
        />
      )}

      {/* Bulk Export Dialog */}
      {bulkExportOpen && (
        <CustomExportDialog
          open={bulkExportOpen}
          onClose={() => setBulkExportOpen(false)}
          jobId={id}
          leadIds={selectedLeads}
        />
      )}

      {/* Filtered Custom Export Dialog (from Leads tab toolbar) */}
      {filteredExportOpen && (
        <CustomExportDialog
          open={filteredExportOpen}
          onClose={() => {
            setFilteredExportOpen(false);
            setFilteredExportLeadIds([]);
          }}
          jobId={id}
          leadIds={filteredExportLeadIds}
        />
      )}

      {/* Browserless LiveURL Intervention Dialog */}
      {/* Shows when manual verification is needed during enrichment */}
      <InterventionDialog
        jobId={parseInt(id, 10)}
        leadId={enrichingLeadId}
        open={!!enrichingLeadId && !!currentTaskId}
        pollingInterval={5000}
        onComplete={() => {
          // Refresh data after intervention completes
          queryClient.invalidateQueries(['jobLeads', id]);
          setTaskSnackbar({
            open: true,
            message: 'Verification completed! Enrichment will continue.',
            severity: 'success'
          });
        }}
        onCancel={() => {
          // User skipped intervention
          setTaskSnackbar({
            open: true,
            message: 'Verification skipped. Enrichment will use fallback methods.',
            severity: 'info'
          });
        }}
      />

      {/* Task Snackbar */}
      <Snackbar
        open={taskSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setTaskSnackbar({ ...taskSnackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setTaskSnackbar({ ...taskSnackbar, open: false })} 
          severity={taskSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {taskSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JobDetail; 
